import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
// Constants - Functions
import { WEB_SERVICE_MIMS } from "../../../../../../config/constants";
import { isMotorProtocol, isCognitiveProtocol } from "../../../../../../config/validateProtocols";
// Components
import {
  WeeksContainer,
  WeekTitle,
  WeeksSummaryAligner,
  WeeksSummaryBox,
  DataNumber,
  ActivityTypeText,
  DataType,
  ActionButtonsDiv,
  EditButton,
  AcceptButton,
  AcceptedTextDiv,
  CheckImg,
  AcceptedText,
} from "./styles";
import UseFetch from "../../../../../../config/useFetch";
import PrescriptionsPopup from "../prescriptionsPopup";
// Assets
import CheckGray from "../../../../../../assets/icons/check_gray.svg";

const WeeklySummary = ({
  hasRecsys,
  prescriptions = [],
  isEditableWeek,
  isSomePrescriptionInserted,
  setIsSomePrescriptionInserted,
  datesPerWeek,
  targetedWeek,
  startingDate,
  refreshPrescriptions,
  setRefreshPrescriptions,
  isShowingPrescriptionsPopup,
  setIsShowingPrescriptionsPopup,
}) => {
  const [cognitiveActivities, setCognitiveActivities] = useState(0);
  const [motorActivities, setMotorActivities] = useState(0);
  const [totalActivities, setTotalActivities] = useState(0);
  const [cognitiveMinutes, setCognitiveMinutes] = useState(0);
  const [motorMinutes, setMotorMinutes] = useState(0);
  const [totalMinutes, setTotalMinutes] = useState(0);

  const [activePrescriptions, setActivePrescriptions] = useState([]);
  const [prescriptionStartingDate] = useState(moment(`${datesPerWeek[targetedWeek]} 10:00`, "DD-MM-YYYY HH:mm").format("YYYY-MM-DD HH:mm"));
  const [prescriptionEndingDate] = useState(
    moment(`${datesPerWeek[targetedWeek]} 10:00`, "DD-MM-YYYY HH:mm").add(7, "days").format("YYYY-MM-DD HH:mm"),
  );
  let [prescriptionsToDatabase, setPrescriptionsToDatabase] = useState([]);
  const { patientId } = useParams();

  // Set activities and trained minutes data
  useEffect(() => {
    const getActivities = (checkProtocolType) => {
      return activePrescriptions.reduce((accumulator, prescription) => {
        if (checkProtocolType(prescription.PROTOCOL_ID)) accumulator++;
        return accumulator;
      }, 0);
    };

    const getTrainedMinutes = (checkProtocolType) => {
      return activePrescriptions.reduce((accumulator, prescription) => {
        if (checkProtocolType(prescription.PROTOCOL_ID))
          accumulator += (prescription.PRESCRIPTION_DURATION / 60) * prescription.PRESCRIPTION_DAYS.length;
        return accumulator;
      }, 0);
    };

    const getTotalTrainedMinutes = () => {
      return activePrescriptions.reduce((accumulator, prescription) => {
        accumulator += (prescription.PRESCRIPTION_DURATION / 60) * prescription.PRESCRIPTION_DAYS.length;
        return accumulator;
      }, 0);
    };

    const cognitiveActivities = getActivities(isCognitiveProtocol);
    const motorActivities = getActivities(isMotorProtocol);

    const cognitiveMinutes = getTrainedMinutes(isCognitiveProtocol);
    const motorMinutes = getTrainedMinutes(isMotorProtocol);

    const totalTrainedMinutes = getTotalTrainedMinutes();

    setCognitiveActivities(cognitiveActivities);
    setMotorActivities(motorActivities);
    setTotalActivities(activePrescriptions.length);

    setCognitiveMinutes(cognitiveMinutes);
    setMotorMinutes(motorMinutes);
    setTotalMinutes(totalTrainedMinutes);

    return () => {
      setCognitiveActivities(0);
      setMotorActivities(0);
      setTotalActivities(0);
      setCognitiveMinutes(0);
      setMotorMinutes(0);
      setTotalMinutes(0);
    };
  }, [activePrescriptions, prescriptions]);

  // Set recommended prescriptions to save in database
  useEffect(() => {
    const activePrescriptions = prescriptions.filter((prescription) => prescription.STATUS !== "REMOVED");

    let prescriptionsToDatabase = [];
    for (const prescription of activePrescriptions) {
      const protocolId = prescription.PROTOCOL_ID;
      const prescriptionDuration = prescription.PRESCRIPTION_DURATION;
      for (const weekday of prescription.PRESCRIPTION_DAYS) {
        const prescription = {
          PROTOCOL_ID: protocolId,
          WEEKDAY: weekday,
          STARTING_DATE: prescriptionStartingDate,
          ENDING_DATE: prescriptionEndingDate,
          SESSION_DURATION: prescriptionDuration,
        };
        prescriptionsToDatabase.push(prescription);
      }
    }

    setActivePrescriptions(activePrescriptions);
    setPrescriptionsToDatabase(prescriptionsToDatabase);

    return () => {
      setActivePrescriptions([]);
      setPrescriptionsToDatabase([]);
    };
  }, [prescriptions, prescriptionStartingDate, prescriptionEndingDate]);

  const setEmptyValue = (value) => (!Number(value) ? "-" : value);

  const saveRecommendedPrescriptions = async () => {
    await UseFetch("put", `${WEB_SERVICE_MIMS}/recsys/update-prescriptions-status/${patientId}`, new AbortController(), {
      startingDate: prescriptionStartingDate,
      endingDate: prescriptionEndingDate,
      status: "Accepted",
    }).catch((error) => {
      toast.error("Error sending data, please try again");
      console.log("Update prescriptions status: ", error);
    });

    await UseFetch("post", `${WEB_SERVICE_MIMS}/prescriptions/add/${patientId}/plus`, new AbortController(), prescriptionsToDatabase)
      .then(({ data }) => {
        if (data.success) {
          toast.success("The recommended prescriptions have been accepted");
          setIsSomePrescriptionInserted(true);
        } else {
          toast.error("Error sending data, please try again");
        }
      })
      .catch((error) => {
        toast.error("Error sending data, please try again");
        console.log("Add prescriptions: ", error);
      });
  };

  const renderActionButtons = () => {
    return (
      <ActionButtonsDiv>
        <EditButton onClick={() => setIsShowingPrescriptionsPopup(true)}>Edit prescription</EditButton>
        {hasRecsys ? (
          isSomePrescriptionInserted ? (
            <AcceptedTextDiv>
              <CheckImg src={CheckGray} alt="check-ico" />
              <AcceptedText>Accepted prescription</AcceptedText>
            </AcceptedTextDiv>
          ) : (
            <>{Boolean(prescriptions.length) && <AcceptButton onClick={saveRecommendedPrescriptions}>Accept prescription</AcceptButton>}</>
          )
        ) : null}
      </ActionButtonsDiv>
    );
  };

  const showActionButtons =
    moment(startingDate, "DD/MM/YYYY").format("dddd") === moment().format("dddd") || moment(startingDate, "DD/MM/YYYY").isAfter(moment());

  return (
    <>
      <WeeksContainer>
        <WeekTitle>
          <span style={{ fontWeight: "bold" }}>Weekly summary</span>
        </WeekTitle>
        <WeeksSummaryAligner>
          <WeeksSummaryBox>
            <DataNumber>{setEmptyValue(cognitiveActivities)}</DataNumber>
            <ActivityTypeText color="#F15A24">COGNITIVE</ActivityTypeText>
            <DataType>activities</DataType>
          </WeeksSummaryBox>

          <WeeksSummaryBox>
            <DataNumber>{setEmptyValue(motorActivities)}</DataNumber>
            <ActivityTypeText color="#22B573">MOTOR</ActivityTypeText>
            <DataType>activities</DataType>
          </WeeksSummaryBox>

          <WeeksSummaryBox>
            <DataNumber>{setEmptyValue(totalActivities)}</DataNumber>
            <ActivityTypeText color="#000000">TOTAL</ActivityTypeText>
            <DataType>activities</DataType>
          </WeeksSummaryBox>

          <WeeksSummaryBox>
            <DataNumber>{setEmptyValue(cognitiveMinutes)}</DataNumber>
            <ActivityTypeText color="#F15A24">COGNITIVE</ActivityTypeText>
            <DataType>trained minutes</DataType>
          </WeeksSummaryBox>

          <WeeksSummaryBox>
            <DataNumber>{setEmptyValue(motorMinutes)}</DataNumber>
            <ActivityTypeText color="#22B573">MOTOR</ActivityTypeText>
            <DataType>trained minutes</DataType>
          </WeeksSummaryBox>

          <WeeksSummaryBox>
            <DataNumber>{setEmptyValue(totalMinutes)}</DataNumber>
            <ActivityTypeText color="#000000">TOTAL</ActivityTypeText>
            <DataType>trained minutes</DataType>
          </WeeksSummaryBox>
        </WeeksSummaryAligner>
        {isEditableWeek && showActionButtons && renderActionButtons()}
      </WeeksContainer>
      {isShowingPrescriptionsPopup && (
        <PrescriptionsPopup
          prescriptions={activePrescriptions}
          currentPrescriptions={prescriptionsToDatabase}
          prescriptionStartingDate={prescriptionStartingDate}
          prescriptionEndingDate={prescriptionEndingDate}
          isSomePrescriptionInserted={isSomePrescriptionInserted}
          isShowingPrescriptionsPopup={isShowingPrescriptionsPopup}
          setIsShowingPrescriptionsPopup={setIsShowingPrescriptionsPopup}
          refreshPrescriptions={refreshPrescriptions}
          setRefreshPrescriptions={setRefreshPrescriptions}
          hasRecsys={hasRecsys}
        />
      )}
    </>
  );
};

export default WeeklySummary;
